'use client'

import * as React from 'react'
import { Dialog as DialogPrimitive } from '@base-ui/react/dialog'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { ComboboxPortalContainerContext } from '@/components/ui/combobox-portal-context'
import { XIcon } from 'lucide-react'

function Dialog({ ...props }: DialogPrimitive.Root.Props) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />
}

function DialogTrigger({ ...props }: DialogPrimitive.Trigger.Props) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />
}

function DialogPortal({ ...props }: DialogPrimitive.Portal.Props) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />
}

function DialogClose({ ...props }: DialogPrimitive.Close.Props) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />
}

function DialogOverlay({ className, ...props }: DialogPrimitive.Backdrop.Props) {
  return (
    <DialogPrimitive.Backdrop
      data-slot="dialog-overlay"
      className={cn(
        'fixed inset-0 isolate z-50 bg-black/10 duration-100 supports-backdrop-filter:backdrop-blur-xs data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0',
        className,
      )}
      {...props}
    />
  )
}

function DialogContent({
  className,
  children,
  showCloseButton = true,
  forceBackdrop = false,
  nested = false,
  ...props
}: DialogPrimitive.Popup.Props & {
  showCloseButton?: boolean
  /**
   * Render the dim/blur even when this dialog is NESTED inside another one. base-ui drops a nested backdrop by
   * default (so dims don't stack), which is right for a dialog opened from a dialog — but wrong when the parent
   * is a surface that stays fully visible behind it, e.g. the mobile user-menu sheet: with no backdrop of its
   * own, the dialog floats over crisp, undimmed content. Pass this there, and have the parent surface's own
   * backdrop step aside (see drawer.tsx) so only one blur is ever on screen.
   */
  forceBackdrop?: boolean
  /**
   * This dialog is a FORM opened on top of another form's dialog (the Puesto/Equipo forms launched from the
   * employee form). Without a backdrop of its own it lands on the same flat surface as its parent and the two
   * sets of fields read as one long form. Dims + blurs the parent dialog and lifts this one with a shadow, so
   * which fields belong to which form is unambiguous.
   */
  nested?: boolean
}) {
  const [portalContainer, setPortalContainer] = React.useState<HTMLElement | null>(null)
  const closeButtonRef = React.useRef<HTMLButtonElement>(null)
  return (
    <DialogPortal>
      <DialogOverlay
        forceRender={forceBackdrop || nested}
        className={cn(nested && 'bg-black/50 supports-backdrop-filter:backdrop-blur-sm')}
      />
      <DialogPrimitive.Popup
        ref={setPortalContainer}
        data-slot="dialog-content"
        initialFocus={showCloseButton ? closeButtonRef : undefined}
        className={cn(
          'fixed top-1/2 left-1/2 z-50 grid w-full max-w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 gap-4 rounded-xl bg-background p-4 text-sm ring-1 ring-foreground/10 duration-100 outline-none sm:max-w-sm data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95',
          '*:data-[base-ui-portal]:contents',
          nested && 'ring-foreground/25 shadow-2xl',
          className,
        )}
        {...props}
      >
        <ComboboxPortalContainerContext.Provider value={portalContainer}>
          {children}
        </ComboboxPortalContainerContext.Provider>
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot="dialog-close"
            render={
              <Button ref={closeButtonRef} variant="ghost" className="absolute top-2 right-2 z-20" size="icon-sm" />
            }
          >
            <XIcon />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Popup>
    </DialogPortal>
  )
}

function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="dialog-header" className={cn('flex flex-col gap-2', className)} {...props} />
}

function DialogFooter({
  className,
  showCloseButton = false,
  children,
  ...props
}: React.ComponentProps<'div'> & {
  showCloseButton?: boolean
}) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn('-mx-4 -mb-4 flex flex-col-reverse gap-2 rounded-b-xl p-4 sm:flex-row sm:justify-end', className)}
      {...props}
    >
      {children}
      {showCloseButton && <DialogPrimitive.Close render={<Button variant="outline" />}>Close</DialogPrimitive.Close>}
    </div>
  )
}

function DialogTitle({ className, ...props }: DialogPrimitive.Title.Props) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn('text-base leading-none font-medium', className)}
      {...props}
    />
  )
}

function DialogDescription({ className, ...props }: DialogPrimitive.Description.Props) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn(
        'text-sm text-muted-foreground *:[a]:underline *:[a]:underline-offset-3 *:[a]:hover:text-foreground',
        className,
      )}
      {...props}
    />
  )
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
}
